/**
 * Policy Enforcer — validates skill invocations against the 5-stage policy pipeline.
 *
 * Pipeline stages:
 *   1. Parse    — extract action intent from request
 *   2. Validate — schema validation of policy rules
 *   3. Enforce  — apply tool, model, and rate policies
 *   4. Log      — emit av.policy.evaluate telemetry span
 *   5. Report   — aggregate policy metrics
 *
 * Integration points:
 *   - AgentVaultMcpServer wraps tool handlers with enforce()
 *   - Channel.deliver() checks before forwarding sensitive messages
 *   - agentvault_check_policy MCP tool calls evaluate()
 */
import type { SkillDefinition } from "./mcp-server.js";
import {
  buildPolicyViolationSpan,
  type TelemetrySpan,
} from "@agentvault/crypto";

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface PolicyContext {
  skillName: string;
  toolName?: string;
  model?: string;
  args?: Record<string, unknown>;
  agentId?: string;
}

export interface PolicyResult {
  allowed: boolean;
  violations: PolicyViolation[];
  stage: "parse" | "validate" | "enforce" | "log" | "report";
}

export interface PolicyViolation {
  ruleId: string;
  scope: "tool" | "model" | "rate" | "network" | "custom";
  action: "block" | "warn" | "log";
  type: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface PolicyMetrics {
  totalEvaluations: number;
  totalBlocks: number;
  totalWarnings: number;
  bySkill: Record<string, { evaluations: number; blocks: number }>;
  byRule: Record<string, number>;
}

/* -------------------------------------------------------------------------- */
/*  PolicyEnforcer                                                             */
/* -------------------------------------------------------------------------- */

export class PolicyEnforcer {
  private skills: Map<string, SkillDefinition> = new Map();
  private metrics: PolicyMetrics = {
    totalEvaluations: 0,
    totalBlocks: 0,
    totalWarnings: 0,
    bySkill: {},
    byRule: {},
  };
  private spanBuffer: TelemetrySpan[] = [];

  /**
   * Register a skill definition for policy evaluation.
   */
  registerSkill(skill: SkillDefinition): void {
    this.skills.set(skill.name, skill);
  }

  /**
   * Full 5-stage policy pipeline evaluation.
   */
  evaluate(ctx: PolicyContext): PolicyResult {
    this.metrics.totalEvaluations++;
    const skillMetrics = this.metrics.bySkill[ctx.skillName] ??= { evaluations: 0, blocks: 0 };
    skillMetrics.evaluations++;

    const violations: PolicyViolation[] = [];

    // Stage 1: Parse — extract intent
    const skill = this.skills.get(ctx.skillName);

    // Stage 2: Validate — check that policy rules are well-formed
    // (no-op for now; future: validate against JSON Schema for policy rules)

    // Stage 3: Enforce — apply tool, model, and rate policies
    if (skill) {
      // Tool deny-list check
      if (ctx.toolName && skill.toolsDenied?.length) {
        if (skill.toolsDenied.includes(ctx.toolName)) {
          violations.push({
            ruleId: `deny:${ctx.skillName}:${ctx.toolName}`,
            scope: "tool",
            action: "block",
            type: "forbidden_tool",
            message: `Tool "${ctx.toolName}" is forbidden for skill "${ctx.skillName}"`,
          });
        }
      }

      // Tool allow-list check (if allowlist exists, deny unlisted)
      if (ctx.toolName && skill.toolsAllowed?.length) {
        if (!skill.toolsAllowed.includes(ctx.toolName)) {
          violations.push({
            ruleId: `allow:${ctx.skillName}:${ctx.toolName}`,
            scope: "tool",
            action: "block",
            type: "tool_not_allowed",
            message: `Tool "${ctx.toolName}" is not in the allowed list for skill "${ctx.skillName}"`,
          });
        }
      }

      // Model routing check
      if (ctx.model && skill.allowedModels?.length) {
        if (!skill.allowedModels.includes(ctx.model)) {
          violations.push({
            ruleId: `model:${ctx.skillName}:${ctx.model}`,
            scope: "model",
            action: "block",
            type: "model_not_allowed",
            message: `Model "${ctx.model}" is not allowed for skill "${ctx.skillName}". Allowed: ${skill.allowedModels.join(", ")}`,
          });
        }
      }
    }

    const blocked = violations.some((v) => v.action === "block");

    // Stage 4: Log — emit telemetry span for each violation
    for (const v of violations) {
      this.metrics.byRule[v.ruleId] = (this.metrics.byRule[v.ruleId] ?? 0) + 1;
      if (v.action === "block") this.metrics.totalBlocks++;
      if (v.action === "warn") this.metrics.totalWarnings++;

      this.spanBuffer.push(
        buildPolicyViolationSpan({
          ruleId: v.ruleId,
          policyScope: v.scope,
          actionTaken: v.action,
          violationType: v.type,
          targetTool: ctx.toolName,
          targetModel: ctx.model,
          skillName: ctx.skillName,
        }),
      );
    }

    if (blocked) {
      skillMetrics.blocks++;
    }

    // Stage 5: Report — metrics are accumulated, drain via getMetrics()

    return {
      allowed: !blocked,
      violations,
      stage: "report",
    };
  }

  /**
   * Wrap an MCP tool handler with policy enforcement.
   * Returns a function that checks policy before calling the original handler.
   */
  wrapHandler(
    skillName: string,
    handler: (args: Record<string, unknown>) => Promise<unknown>,
  ): (args: Record<string, unknown>) => Promise<unknown> {
    return async (args: Record<string, unknown>) => {
      const result = this.evaluate({
        skillName,
        args,
      });

      if (!result.allowed) {
        return {
          blocked: true,
          violations: result.violations.map((v) => ({
            rule: v.ruleId,
            reason: v.message,
            scope: v.scope,
          })),
        };
      }

      return handler(args);
    };
  }

  /**
   * Get accumulated policy metrics.
   */
  getMetrics(): PolicyMetrics {
    return { ...this.metrics };
  }

  /**
   * Drain buffered telemetry spans.
   */
  drainSpans(): TelemetrySpan[] {
    const spans = this.spanBuffer;
    this.spanBuffer = [];
    return spans;
  }

  /**
   * Reset all metrics (for testing).
   */
  resetMetrics(): void {
    this.metrics = {
      totalEvaluations: 0,
      totalBlocks: 0,
      totalWarnings: 0,
      bySkill: {},
      byRule: {},
    };
  }
}
